import { useDispatch, useSelector } from 'react-redux';
import UserProfileService from '../services/UserProfileService';
import { loadUserProfile, updateUserPersonalInfo, updateUserProfilePicture } from '../store/slices/userProfile/userProfileSlice';
import type { RootState, AppDispatch } from '../store/store';
import useAlerts from './useAlerts';
import useApiErrorHandler from './useApiErrorHandler';

export const useUserProfile = () => {
    const dispatch: AppDispatch = useDispatch();
    const { show: showAlert } = useAlerts();
    const { handle: handleApiError } = useApiErrorHandler();
    const userProfile = useSelector((state: RootState) => state.userProfile.userProfile);

    const fetchUserProfile = async (email: string) => {
        try {
            const response = await UserProfileService.getUserByEmail(email);
            dispatch(loadUserProfile(response.data));
        } catch (error) {
            showAlert('error', 'Error', 'No se pudo cargar el perfil de usuario.');
        }
    };

    const handleUpdatePersonalInfo = async (id: number, data: any) => {
        try {
            const result = await UserProfileService.updatePersonalInfo(id, data);
            const { title, message, statusCode } = result.data;

            // Verificamos el código de estado para determinar qué alerta mostrar
            if (statusCode === 200) {
                dispatch(updateUserPersonalInfo(data));
                showAlert("success", title, message);
            } else if (statusCode === 400) {
                showAlert("error", title, message);
            } else {
                showAlert("error", "Error desconocido", "Ocurrió un error inesperado.");
            }
        } catch (error) {
            showAlert('error', 'Error', 'No se pudo actualizar la información personal.');
        }
    };

    const handleUpdatePassword = async (id: number, data: any) => {
        try {
            const result = await UserProfileService.updatePassword(id, data);
            const { title, message, statusCode } = result.data ?? {};

            if (statusCode === 200) {
                showAlert("success", title || "Contraseña actualizada", message || "La contraseña se actualizó correctamente.");
                return true;
            }

            showAlert("error", title || "Error", message || "No se pudo actualizar la contraseña.");
            return false;
        } catch (error) {
            // El backend devuelve CustomResponse con el detalle real (400: contraseña
            // actual incorrecta / nueva igual a la anterior, 404: usuario no encontrado).
            handleApiError(error, 'No se pudo actualizar la contraseña.');
            return false;
        }
    }

    const handleUploadProfilePicture = async (id: number, file: File) => {
        try {
            const result = await UserProfileService.uploadProfilePicture(id, file);
            const { title, message, statusCode, data } = result.data;

            if (statusCode === 200) {
                const newProfileImage = data; // Nueva imagen en base64 o URL

                // 🔥 Generamos una nueva clave basada en la imagen
                const newKey = `profileImage-${id}-${btoa(newProfileImage).slice(-10)}`;

                // ✅ Guardamos en sessionStorage
                sessionStorage.setItem(`profileImage-${id}`, newProfileImage);
                sessionStorage.setItem(`profileImageKey-${id}`, newKey);

                // ✅ Actualizamos Redux
                dispatch(updateUserProfilePicture(newProfileImage));

                showAlert("success", title, message);
            } else {
                showAlert("error", title, message);
            }
        } catch (error) {
            showAlert("error", "Error", "No se pudo actualizar la foto de perfil.");
        }
    };


    const handleGetProfilePicture = async (userId: number) => {
        try {
            // 🔥 Buscar primero en sessionStorage
            const storedKey = sessionStorage.getItem(`profileImageKey-${userId}`);
            const storedImage = sessionStorage.getItem(`profileImage-${userId}`);

            // ✅ Si ya existe en cache, usarla directamente
            if (storedKey && storedImage) {
                dispatch(updateUserProfilePicture(storedImage));
                return storedImage;
            }

            // 🟢 Si no existe en cache, pedir al backend
            const imageBlob = await UserProfileService.getProfilePictureUrl(userId);

            // ✅ Si no hay imagen (404 controlado), no mostrar error
            if (!imageBlob) {
                dispatch(updateUserProfilePicture(null));
                return null;
            }

            // 🔄 Convertir Blob → Base64
            return await new Promise<string>((resolve) => {
                const reader = new FileReader();

                reader.onloadend = () => {
                    const base64Image = reader.result as string;

                    // 🔥 Crear nueva key única basada en la imagen
                    const newKey = `profileImage-${userId}-${btoa(base64Image).slice(-10)}`;

                    // ✅ Guardar en sessionStorage
                    sessionStorage.setItem(`profileImage-${userId}`, base64Image);
                    sessionStorage.setItem(`profileImageKey-${userId}`, newKey);

                    // ✅ Actualizar Redux
                    dispatch(updateUserProfilePicture(base64Image));

                    resolve(base64Image);
                };

                reader.readAsDataURL(imageBlob);
            });

        } catch (error: any) {
            // 🚨 Solo loguear errores reales, no 404 normales
            if (error.response?.status !== 404) {
                console.error("Error real al obtener la imagen de perfil:", error);
            }

            dispatch(updateUserProfilePicture(null));
            return null;
        }
    };


    const handleDeleteProfilePicture = async (id: number) => {
        try {
            const result = await UserProfileService.deleteProfilePicture(id);
            const { title, message, statusCode } = result.data;

            if (statusCode === 200) {
                // ✅ limpiar imagen en Redux
                dispatch(updateUserProfilePicture(""));

                // ✅ limpiar cache local
                sessionStorage.removeItem(`profileImage-${id}`);
                sessionStorage.removeItem(`profileImageKey-${id}`);

                showAlert("success", title, message);
            } else {
                showAlert("error", title, message);
            }
        } catch (error) {
            showAlert(
                "error",
                "Error",
                "No se pudo eliminar la foto de perfil."
            );
        }
    };

    return {
        userProfile,
        fetchUserProfile,
        handleUpdatePersonalInfo,
        handleUpdatePassword,
        // handleEnableGoogleAuth,
        handleUploadProfilePicture,
        handleGetProfilePicture,
        handleDeleteProfilePicture,
    };
};
